/**
 * Zustand 게임 세션 상태 관리 스토어
 * - 현재 플레이 중인 문제집 및 페이지
 * - 점수, 콤보, HP 관리
 */

import { create } from 'zustand';

/**
 * 문제 정보 (간단한 타입)
 */
interface SimpleProblem {
  id: string;
  problem_number: string;
  answer_format: string;
  can_submit: boolean;
  next_attempt_number: number | null;
}

/**
 * 게임 세션 상태 인터페이스
 */
interface GameState {
  // 현재 세션 정보
  currentWorkbook: string | null;
  currentPage: number;
  totalPages: number;

  // 문제 정보
  problems: SimpleProblem[];
  currentProblemIndex: number;
  isRetryMode: boolean;

  // 게임 통계
  score: number;
  combo: number;
  hp: number;
  maxHp: number;

  // 진행 상태
  isPlaying: boolean;

  // Actions
  startSession: (workbookName: string, totalPages: number) => void;
  setPage: (page: number) => void;
  loadProblems: (problems: SimpleProblem[], isRetryMode: boolean) => void;
  setCurrentProblemIndex: (index: number) => void;
  addScore: (points: number) => void;
  incrementCombo: () => void;
  resetCombo: () => void;
  decreaseHP: (amount: number) => void;
  increaseHP: (amount: number) => void;
  resetSession: () => void;
  endSession: () => void;
}

/**
 * 게임 스토어
 */
export const useGameStore = create<GameState>((set) => ({
  // 초기 상태
  currentWorkbook: null,
  currentPage: 1,
  totalPages: 0,
  problems: [],
  currentProblemIndex: 0,
  isRetryMode: false,
  score: 0,
  combo: 0,
  hp: 10,
  maxHp: 10,
  isPlaying: false,

  /**
   * 게임 세션 시작
   * @param workbookName - 문제집명
   * @param totalPages - 전체 페이지 수
   */
  startSession: (workbookName: string, totalPages: number) => {
    set({
      currentWorkbook: workbookName,
      currentPage: 1,
      totalPages,
      score: 0,
      combo: 0,
      hp: 10,
      maxHp: 10,
      isPlaying: true,
    });
  },

  /**
   * 현재 페이지 설정
   */
  setPage: (page: number) => {
    set({ currentPage: page });
  },

  /**
   * 문제 목록 로드
   * @param problems - 문제 목록
   * @param isRetryMode - 재채점 모드 여부
   */
  loadProblems: (problems, isRetryMode) => {
    set({
      problems,
      isRetryMode,
      currentProblemIndex: 0,
      hp: problems.length,
      maxHp: problems.length,
    });
  },

  /**
   * 현재 문제 인덱스 설정
   */
  setCurrentProblemIndex: (index) => {
    set({ currentProblemIndex: index });
  },

  /**
   * 점수 추가
   * @param points - 추가할 점수
   */
  addScore: (points: number) => {
    set((state) => ({ score: state.score + points }));
  },

  /**
   * 콤보 증가
   */
  incrementCombo: () => {
    set((state) => ({ combo: state.combo + 1 }));
  },

  /**
   * 콤보 초기화 (오답 시)
   */
  resetCombo: () => {
    set({ combo: 0 });
  },

  /**
   * HP 감소
   * @param amount - 감소할 HP
   */
  decreaseHP: (amount: number) => {
    set((state) => ({
      hp: Math.max(0, state.hp - amount),
    }));
  },

  /**
   * HP 증가
   * @param amount - 증가할 HP
   */
  increaseHP: (amount: number) => {
    set((state) => ({
      hp: Math.min(state.maxHp, state.hp + amount),
    }));
  },

  /**
   * 세션 초기화 (재시작)
   */
  resetSession: () => {
    set((state) => ({
      currentPage: 1,
      score: 0,
      combo: 0,
      hp: state.maxHp,
    }));
  },

  /**
   * 세션 종료
   */
  endSession: () => {
    set({
      currentWorkbook: null,
      currentPage: 1,
      totalPages: 0,
      problems: [],
      currentProblemIndex: 0,
      isRetryMode: false,
      score: 0,
      combo: 0,
      hp: 10,
      isPlaying: false,
    });
  },
}));
